/**
 * products.js — the products / services index.
 *
 * Fixes carried over from the brief:
 *   * The page hero rendered as an empty white band. It now always paints a
 *     surface (see .nv-page-hero in _hero.css) and carries a breadcrumb,
 *     title, subtitle and result count.
 *   * The search field was an unstyled input; it is now .nv-searchbar with a
 *     clear button and a proper submit affordance.
 *   * Spacing came from ad-hoc inline padding. Everything now sits on the
 *     section rhythm tokens, so the margins match the rest of the site.
 *   * Prices are never shown — products are quoted on request.
 */

import { esc, img, icon, truncate, emptyState } from '../lib/dom.js';
import { gettext, num, localePath } from '../lib/i18n.js';

const TYPE_LABELS = () => ({
  product: gettext('Product'),
  service: gettext('Service'),
  course: gettext('Course'),
  book: gettext('Book'),
});

const SERVICE_LABELS = () => ({
  table_game: gettext('Table game session'),
  training_course: gettext('Training course'),
  org_game_design: gettext('Organisational game design'),
});

function productCard(product) {
  const typeLabel = TYPE_LABELS()[product.product_type] || '';
  const serviceLabel = SERVICE_LABELS()[product.service_type] || '';
  const cover = product.images?.[0]?.image || product.image;

  return `
    <a href="${esc(localePath(`/products/${product.slug}`))}" class="nv-product-card">
      <div class="nv-product-card__media">
        ${cover ? img(cover, product.title)
                : `<span class="nv-card__placeholder">${icon('box-open')}</span>`}
        ${typeLabel ? `
          <span class="nv-product-card__tag">
            <span class="nv-badge nv-badge--accent">${esc(typeLabel)}</span>
          </span>` : ''}
      </div>
      <div class="nv-product-card__body">
        <h3 class="nv-product-card__title">${esc(product.title)}</h3>
        ${product.short_description
          ? `<p class="nv-product-card__text">${esc(truncate(product.short_description, 16))}</p>`
          : ''}
        <div class="nv-product-card__footer">
          <span class="nv-product-card__quote">
            ${icon('file-invoice')}
            <span>${gettext('Request a quote')}</span>
          </span>
          ${serviceLabel ? `<span class="nv-badge nv-badge--neutral">${esc(serviceLabel)}</span>` : ''}
        </div>
      </div>
    </a>`;
}

function renderFilters(categories, active) {
  if (!categories?.length) return '';

  return `
    <div class="nv-filters" role="group" aria-label="${gettext('Categories')}">
      <button type="button" class="nv-chip ${!active ? 'is-active' : ''}" data-category="">
        <span>${gettext('All')}</span>
      </button>
      ${categories.map((c) => `
        <button type="button" class="nv-chip ${active === c.slug ? 'is-active' : ''}"
                data-category="${esc(c.slug)}">
          <span>${esc(c.name)}</span>
          ${c.count !== undefined
      ? `<span class="nv-chip__count nv-num">${num(c.count)}</span>` : ''}
        </button>`).join('')}
    </div>`;
}

function renderPagination(page, totalPages) {
  if (!totalPages || totalPages <= 1) return '';

  const items = [];
  const push = (p, label = null, active = false) => {
    items.push(`
      <button type="button" class="nv-pagination__item ${active ? 'is-active' : ''}"
              data-page="${p}" ${active ? 'aria-current="page"' : ''}>
        ${label || `<span class="nv-num">${num(p)}</span>`}
      </button>`);
  };

  items.push(`
    <button type="button" class="nv-pagination__item ${page <= 1 ? 'is-disabled' : ''}"
            data-page="${page - 1}" aria-label="${gettext('Previous')}">
      <i class="fa-solid fa-chevron-left nv-icon-flip" aria-hidden="true"></i>
    </button>`);

  const window_ = 2;
  for (let p = 1; p <= totalPages; p += 1) {
    if (p === 1 || p === totalPages || Math.abs(p - page) <= window_) {
      push(p, null, p === page);
    } else if (Math.abs(p - page) === window_ + 1) {
      items.push('<span class="nv-pagination__ellipsis">…</span>');
    }
  }

  items.push(`
    <button type="button" class="nv-pagination__item ${page >= totalPages ? 'is-disabled' : ''}"
            data-page="${page + 1}" aria-label="${gettext('Next')}">
      <i class="fa-solid fa-chevron-right nv-icon-flip" aria-hidden="true"></i>
    </button>`);

  return `<nav class="nv-pagination" aria-label="${gettext('Pagination')}">${items.join('')}</nav>`;
}

export function renderProductList(data, params = {}) {
  const products = data.products || [];
  const total = data.total || 0;
  const query = params.q || '';

  const results = products.length
    ? `<div class="nv-grid nv-grid--3">${products.map(productCard).join('')}</div>
       ${renderPagination(data.page || 1, data.total_pages || 1)}`
    : emptyState({
        iconName: 'magnifying-glass',
        title: gettext('No results found'),
        text: query
          ? gettext('Try a different term, or clear the filters.')
          : gettext('No products have been published yet.'),
        action: `<button type="button" class="nv-btn nv-btn--outline" data-reset-filters>
                   ${icon('rotate-left')}<span>${gettext('Clear filters')}</span>
                 </button>`,
      });

  return `
    <section class="nv-page-hero nv-page-hero--products">
      <div class="nv-container nv-page-hero__inner">
        <nav class="nv-breadcrumb" aria-label="${gettext('Breadcrumb')}">
          <a href="${esc(localePath('/'))}">${gettext('Home')}</a>
          <i class="fa-solid fa-chevron-left nv-breadcrumb__sep nv-icon-flip" aria-hidden="true"></i>
          <span>${gettext('Products & Services')}</span>
        </nav>
        <h1 class="nv-page-hero__title">${gettext('Products & Services')}</h1>
        <p class="nv-page-hero__subtitle">
          ${gettext('Systems, training programmes and research-led services from the OPEN Innovation Lab.')}
        </p>
        <div class="nv-page-hero__meta">
          <span class="nv-page-hero__meta-item">
            ${icon('layer-group')}
            <span><span class="nv-num">${num(total)}</span> ${gettext('items')}</span>
          </span>
        </div>
      </div>
    </section>

    <div class="nv-container">
      <div class="nv-products-toolbar">
        <form class="nv-products-toolbar__search" id="product-search">
          <div class="nv-searchbar">
            <i class="fa-solid fa-magnifying-glass nv-searchbar__icon" aria-hidden="true"></i>
            <input type="search" name="q" class="nv-searchbar__input" dir="auto"
                   value="${esc(query)}"
                   placeholder="${gettext('Search products and services…')}"
                   aria-label="${gettext('Search')}">
            ${query ? `
              <button type="button" class="nv-searchbar__clear" data-clear-search
                      aria-label="${gettext('Clear')}">
                <i class="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>` : ''}
            <button type="submit" class="nv-btn nv-btn--primary nv-btn--sm nv-searchbar__submit">
              <span>${gettext('Search')}</span>
            </button>
          </div>
        </form>
        <div class="nv-products-toolbar__meta">
          <span><span class="nv-num">${num(total)}</span> ${gettext('results')}</span>
        </div>
      </div>

      ${renderFilters(data.categories, params.category)}
    </div>

    <section class="nv-section nv-section--flush-top">
      <div class="nv-container">${results}</div>
    </section>`;
}

/** Wire the toolbar. `navigate` comes from the router. */
export function initProductList(scope, navigate) {
  const base = localePath('/products/');

  const go = (updates) => {
    const params = new URLSearchParams(window.location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    // Any filter change resets to the first page.
    if (!('page' in updates)) params.delete('page');
    const qs = params.toString();
    navigate(qs ? `${base}?${qs}` : base);
  };

  scope.querySelector('#product-search')?.addEventListener('submit', (event) => {
    event.preventDefault();
    go({ q: new FormData(event.target).get('q')?.trim() || '' });
  });

  scope.querySelector('[data-clear-search]')?.addEventListener('click', () => go({ q: '' }));

  scope.querySelectorAll('[data-category]').forEach((chip) => {
    chip.addEventListener('click', () => go({ category: chip.dataset.category }));
  });

  scope.querySelectorAll('[data-page]').forEach((btn) => {
    btn.addEventListener('click', () => go({ page: btn.dataset.page }));
  });

  scope.querySelector('[data-reset-filters]')?.addEventListener('click', () => {
    navigate(base);
  });
}
